import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, logout, me } from "../lib/adminApi";
import { toast, Toaster } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_bf3d52d1-4f5f-47e0-aaa8-bb73dc6635d2/artifacts/m948yyw5_LOGO%20IBIALCON.png";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("zones"); // zones | bookings | surcharges
  const [admin, setAdmin] = useState(null);

  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const reloadSettings = async () => {
    const { data } = await adminApi.get("/admin/settings");
    setSettings(data);
  };
  const reloadBookings = async () => {
    const { data } = await adminApi.get("/admin/bookings");
    setBookings(data);
  };

  useEffect(() => {
    me().then(setAdmin).catch(() => nav("/admin/login", { replace: true }));
    reloadSettings().catch(() => {});
    reloadBookings().catch(() => {});
  }, []); // eslint-disable-line

  const onLogout = () => { logout(); nav("/admin/login", { replace: true }); };

  const updateZone = (idx, patch) => {
    setSettings((s) => {
      const zones = [...s.zones];
      zones[idx] = { ...zones[idx], ...patch };
      return { ...s, zones };
    });
  };
  const updateZoneExamples = (idx, text) => {
    updateZone(idx, { examples: text.split(",").map((x) => x.trim()).filter(Boolean) });
  };
  const updateSurcharge = (key, patch) => {
    setSettings((s) => ({ ...s, surcharges: { ...s.surcharges, [key]: { ...s.surcharges[key], ...patch } } }));
  };
  const updateDisposalBlock = (key, patch) => {
    setSettings((s) => ({
      ...s,
      disposal: {
        ...s.disposal,
        [key]: { ...(s.disposal?.[key] || {}), ...patch },
      },
    }));
  };
  const updateDisposalExtra = (value) => {
    setSettings((s) => ({ ...s, disposal: { ...s.disposal, extra_hour: Number(value) } }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await adminApi.put("/admin/settings", {
        zones: settings.zones,
        surcharges: settings.surcharges,
        disposal: settings.disposal,
      });
      setSettings(data);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setSavingSettings(false); }
  };

  const setBookingStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/bookings/${id}/status`, { status });
      toast.success("Estado actualizado");
      reloadBookings();
    } catch { toast.error("Error"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("¿Eliminar esta reserva?")) return;
    try {
      await adminApi.delete(`/admin/bookings/${id}`);
      toast.success("Reserva eliminada");
      reloadBookings();
    } catch { toast.error("Error"); }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-[#070B19] text-[#C9A961] flex items-center justify-center uppercase tracking-[0.25em] text-xs">
        Cargando panel...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B19] text-white">
      <Toaster theme="dark" position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#070B19]/85 backdrop-blur-xl border-b border-[#C9A961]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/95 p-1.5 ring-1 ring-[#C9A961]/30">
              <img src={LOGO} alt="IBIALCON" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-[Playfair_Display] text-lg tracking-wider">IBIALCON</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">Panel Gerente</div>
            </div>
          </a>

          <div className="flex items-center gap-4">
            {admin && <span className="hidden md:inline text-xs text-white/60">{admin.email}</span>}
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="text-xs uppercase tracking-[0.2em] border border-[#C9A961]/40 px-4 py-2 hover:bg-[#C9A961]/10 transition-colors"
            >Cerrar sesión</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-2 border-t border-[#C9A961]/10">
          {[
            { id: "zones", label: "Zonas y precios" },
            { id: "disposal", label: "Disposición" },
            { id: "surcharges", label: "Recargos" },
            { id: "bookings", label: `Reservas (${bookings.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${
                tab === t.id ? "border-[#C9A961] text-[#C9A961]" : "border-transparent text-white/60 hover:text-white"
              }`}
            >{t.label}</button>
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {tab === "zones" && (
          <section>
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-[Playfair_Display] text-3xl md:text-4xl">Zonas y precios</h2>
                <p className="text-white/60 text-sm mt-1">Edita precio, descripción, destinos-ejemplo y activa/desactiva cada zona.</p>
              </div>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="save-zones-btn"
                className="px-6 py-3 bg-[#C9A961] text-[#070B19] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#D4AF37] disabled:opacity-50"
              >{savingSettings ? "Guardando..." : "Guardar cambios"}</button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {settings.zones.map((z, i) => (
                <div key={z.id} data-testid={`zone-row-${z.id}`} className={`grid grid-cols-12 gap-4 items-start border border-[#C9A961]/20 p-5 bg-[#0D1424] ${!z.active ? "opacity-50" : ""}`}>
                  <div className="col-span-1">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-[#C9A961]">Zona</div>
                    <div className="font-[Playfair_Display] text-4xl">{z.id}</div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mb-1">Precio (€)</label>
                    <input
                      data-testid={`zone-price-${z.id}`}
                      type="number"
                      min={0}
                      value={z.price}
                      onChange={(e) => updateZone(i, { price: Number(e.target.value) })}
                      className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 text-xl font-[Playfair_Display] outline-none"
                    />
                  </div>

                  <div className="col-span-4">
                    <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mb-1">Descripción (ES)</label>
                    <input
                      type="text"
                      value={z.description_es}
                      onChange={(e) => updateZone(i, { description_es: e.target.value })}
                      className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 text-sm outline-none"
                    />
                    <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mt-3 mb-1">Descripción (EN)</label>
                    <input
                      type="text"
                      value={z.description_en}
                      onChange={(e) => updateZone(i, { description_en: e.target.value })}
                      className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 text-sm outline-none"
                    />
                  </div>

                  <div className="col-span-4">
                    <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mb-1">Ejemplos (separados por coma)</label>
                    <textarea
                      rows={3}
                      value={(z.examples || []).join(", ")}
                      onChange={(e) => updateZoneExamples(i, e.target.value)}
                      className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 text-sm outline-none resize-none"
                    />
                  </div>

                  <div className="col-span-1 flex flex-col items-end gap-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        data-testid={`zone-active-${z.id}`}
                        type="checkbox"
                        checked={!!z.active}
                        onChange={(e) => updateZone(i, { active: e.target.checked })}
                        className="accent-[#C9A961] w-4 h-4"
                      />
                      <span className="text-[10px] uppercase tracking-[0.2em]">{z.active ? "Activa" : "Inactiva"}</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-white/50 mt-4">Recuerda: tras modificar precios, la web pública refleja los cambios al refrescar. Las reservas ya creadas mantienen su precio original.</p>
          </section>
        )}

        {tab === "disposal" && (
          <section>
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-[Playfair_Display] text-3xl md:text-4xl">Disposición · precios por horas</h2>
                <p className="text-white/60 text-sm mt-1">Vehículo a disposición del cliente. Define precio base por bloque y un % de descuento opcional. La hora extra se suma siempre tras el descuento.</p>
              </div>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="save-disposal-btn"
                className="px-6 py-3 bg-[#C9A961] text-[#070B19] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#D4AF37] disabled:opacity-50"
              >{savingSettings ? "Guardando..." : "Guardar cambios"}</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["3h", "6h", "12h", "24h"].map((k) => {
                const cfg = (settings.disposal && settings.disposal[k]) || { price: 0, discount_pct: 0 };
                const price = Number(cfg.price || 0);
                const pct = Number(cfg.discount_pct || 0);
                const finalPrice = Math.round(price * (1 - pct / 100));
                return (
                  <div key={k} data-testid={`disposal-block-${k}`} className="border border-[#C9A961]/20 bg-[#0D1424] p-6">
                    <div className="flex items-end justify-between mb-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">Bloque</div>
                        <div className="font-[Playfair_Display] text-4xl">{k}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Precio final cliente</div>
                        <div className="font-[Playfair_Display] text-3xl text-[#C9A961]">{finalPrice}€</div>
                        {pct > 0 && (
                          <div className="text-[11px] text-white/40 line-through">{price}€</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FieldNum
                        label="Precio base (€)"
                        value={price}
                        onChange={(v) => updateDisposalBlock(k, { price: Number(v) })}
                        testid={`disposal-price-${k}`}
                        min={0}
                      />
                      <FieldNum
                        label="Descuento (%)"
                        value={pct}
                        onChange={(v) => updateDisposalBlock(k, { discount_pct: Math.max(0, Math.min(100, Number(v))) })}
                        testid={`disposal-discount-${k}`}
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Hora extra */}
              <div className="border border-[#C9A961]/20 bg-[#0D1424] p-6 md:col-span-2">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">Hora extra</div>
                    <div className="font-[Playfair_Display] text-2xl">Precio por hora adicional</div>
                    <p className="text-[11px] text-white/50 mt-1">Se aplica cuando el cliente añade horas extra a un bloque. No se le aplica descuento.</p>
                  </div>
                  <div className="w-40">
                    <FieldNum
                      label="€ / hora extra"
                      value={Number(settings.disposal?.extra_hour ?? 0)}
                      onChange={updateDisposalExtra}
                      testid="disposal-extra-hour"
                      min={0}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === "surcharges" && (
          <section>
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-[Playfair_Display] text-3xl md:text-4xl">Recargos</h2>
                <p className="text-white/60 text-sm mt-1">Se aplican automáticamente al calcular el precio según fecha y hora de la reserva.</p>
              </div>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                data-testid="save-surcharges-btn"
                className="px-6 py-3 bg-[#C9A961] text-[#070B19] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#D4AF37] disabled:opacity-50"
              >{savingSettings ? "Guardando..." : "Guardar cambios"}</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Night */}
              <div className="border border-[#C9A961]/20 bg-[#0D1424] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-[Playfair_Display] text-xl text-[#C9A961]">Recargo nocturno</h3>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="surcharge-night-enabled"
                      type="checkbox"
                      checked={!!settings.surcharges?.night?.enabled}
                      onChange={(e) => updateSurcharge("night", { enabled: e.target.checked })}
                      className="accent-[#C9A961] w-4 h-4"
                    />
                    <span className="text-[10px] uppercase tracking-[0.2em]">{settings.surcharges?.night?.enabled ? "Activo" : "Inactivo"}</span>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldNum label="% Recargo" value={settings.surcharges?.night?.percent ?? 15} onChange={(v) => updateSurcharge("night", { percent: Number(v) })} testid="surcharge-night-percent" />
                  <FieldText label="Desde" type="time" value={settings.surcharges?.night?.start || "22:00"} onChange={(v) => updateSurcharge("night", { start: v })} />
                  <FieldText label="Hasta" type="time" value={settings.surcharges?.night?.end || "06:00"} onChange={(v) => updateSurcharge("night", { end: v })} />
                </div>
                <p className="text-[11px] text-white/50 mt-3">Ej.: 22:00 → 06:00 aplica durante la noche y cruza medianoche automáticamente.</p>
              </div>

              {/* High season */}
              <div className="border border-[#C9A961]/20 bg-[#0D1424] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-[Playfair_Display] text-xl text-[#C9A961]">Temporada alta</h3>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="surcharge-high-enabled"
                      type="checkbox"
                      checked={!!settings.surcharges?.high_season?.enabled}
                      onChange={(e) => updateSurcharge("high_season", { enabled: e.target.checked })}
                      className="accent-[#C9A961] w-4 h-4"
                    />
                    <span className="text-[10px] uppercase tracking-[0.2em]">{settings.surcharges?.high_season?.enabled ? "Activa" : "Inactiva"}</span>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldNum label="% Recargo" value={settings.surcharges?.high_season?.percent ?? 10} onChange={(v) => updateSurcharge("high_season", { percent: Number(v) })} testid="surcharge-high-percent" />
                  <FieldNum label="Mes inicio" value={settings.surcharges?.high_season?.start_month ?? 6} onChange={(v) => updateSurcharge("high_season", { start_month: Number(v) })} min={1} max={12} />
                  <FieldNum label="Mes fin" value={settings.surcharges?.high_season?.end_month ?? 9} onChange={(v) => updateSurcharge("high_season", { end_month: Number(v) })} min={1} max={12} />
                </div>
                <p className="text-[11px] text-white/50 mt-3">Ej.: 6 → 9 aplica del 1 de junio al 30 de septiembre.</p>
              </div>
            </div>
          </section>
        )}

        {tab === "bookings" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-[Playfair_Display] text-3xl md:text-4xl">Reservas</h2>
                <p className="text-white/60 text-sm mt-1">Listado completo · clic en estado para cambiarlo.</p>
              </div>
              <button onClick={reloadBookings} className="text-xs uppercase tracking-[0.2em] border border-[#C9A961]/40 px-4 py-2 hover:bg-[#C9A961]/10">Recargar</button>
            </div>

            {bookings.length === 0 ? (
              <p className="text-white/50 italic">No hay reservas todavía.</p>
            ) : (
              <div className="overflow-x-auto border border-[#C9A961]/20">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#0D1424] text-[10px] uppercase tracking-[0.2em] text-[#C9A961]">
                    <tr>
                      <th className="text-left px-4 py-3">Fecha</th>
                      <th className="text-left px-4 py-3">Cliente</th>
                      <th className="text-left px-4 py-3">Teléfono</th>
                      <th className="text-left px-4 py-3">Trayecto</th>
                      <th className="text-left px-4 py-3">Zona</th>
                      <th className="text-left px-4 py-3">Precio</th>
                      <th className="text-left px-4 py-3">Estado</th>
                      <th className="text-right px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#C9A961]/10 hover:bg-[#0D1424]/60">
                        <td className="px-4 py-3 font-mono text-xs">{b.date} {b.time}</td>
                        <td className="px-4 py-3">{b.name}</td>
                        <td className="px-4 py-3"><a href={`https://wa.me/${(b.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-[#25D366]">{b.phone}</a></td>
                        <td className="px-4 py-3 text-xs max-w-[260px]"><div className="truncate" title={`${b.origin} → ${b.destination}`}>{b.origin} → {b.destination}</div></td>
                        <td className="px-4 py-3">{b.zone}</td>
                        <td className="px-4 py-3 font-semibold text-[#C9A961]">{b.price}€</td>
                        <td className="px-4 py-3">
                          <select
                            value={b.status || "pending"}
                            onChange={(e) => setBookingStatus(b.id, e.target.value)}
                            className="bg-[#070B19] border border-[#C9A961]/30 text-xs px-2 py-1 outline-none"
                          >
                            <option value="pending">Pendiente</option>
                            <option value="confirmed">Confirmada</option>
                            <option value="cancelled">Cancelada</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            data-testid={`booking-delete-${b.id}`}
                            onClick={() => deleteBooking(b.id)}
                            className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
                          >Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function FieldNum({ label, value, onChange, testid, min, max }) {
  return (
    <div>
      <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mb-1">{label}</label>
      <input
        data-testid={testid}
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 outline-none"
      />
    </div>
  );
}
function FieldText({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[9px] uppercase tracking-[0.25em] text-[#C9A961] mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#070B19] border border-[#C9A961]/30 focus:border-[#C9A961] text-white p-2 outline-none"
      />
    </div>
  );
}
